import React, { useState, useEffect, useContext } from 'react';
import RequestService from '../../services/RequestService';
import TableComponent from '../../widgets/TableComponent';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import ContactPageOutlinedIcon from '@mui/icons-material/ContactPageOutlined';

import {
    Typography,
    Breadcrumbs,
    Link,
    Box,
    Grid,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    CircularProgress,
    Stack,
    Snackbar,
    Alert,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { UserContext } from '../../contexts/UserContext';
import CardAssociationDialog from '../../widgets/cards/CardAssociationDialog';
import CardDialog from '../../widgets/cards/CardDialog';
import NewCardDialog from '../../widgets/cards/NewCardDialog';
import CardList from '../../widgets/cards/CardList';

const CardsPage = () => {
    const { user, hasPermission } = useContext(UserContext);
    const [loading, setLoading] = useState(false);
    const [cards, setCards] = useState([]);
    const [statusFilter, setStatusFilter] = useState('');
    const [error, setError] = useState(null);
    const [isAssociateDialogOpen, setIsAssociateDialogOpen] = useState(false);
    const [isCreateCardsDialogOpen, setIsCreateCardsDialogOpen] =
        useState(false);

    const [isCardDialogOpen, setIsCardDialogOpen] = useState(false);
    const [selectedCard, setSelectedCard] = useState(null);

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    // Fetch cards when component mounts or statusFilter changes
    useEffect(() => {
        const fetchCards = async () => {
            try {
                setLoading(true);
                const data = await RequestService.getCardsByCompany(
                    user.company.id,
                    statusFilter
                );
                setCards(Array.isArray(data) ? data : []);
            } catch (err) {
                setError('Erro ao carregar cartões.');
            } finally {
                setLoading(false);
            }
        };

        fetchCards();
    }, [statusFilter]);

    // Handle status filter change
    const handleStatusChange = (event) => {
        setStatusFilter(event.target.value);
    };

    const handleSuccessAssign = async () => {
        const data = await RequestService.getCardsByCompany(
            user.company.id,
            statusFilter
        );
        setCards(Array.isArray(data) ? data : []);

        setSnackbarMessage('Operação concluída com sucesso!');
        setSnackbarOpen(true);
    };

    const handleSuccessCreateCards = async () => {
        try {
            const data = await RequestService.getCardsByCompany(
                user.company.id,
                statusFilter
            );
            setCards(Array.isArray(data) ? data : []);
            setSnackbarMessage('Cartões criados com sucesso!');
            setSnackbarOpen(true);
        } catch (err) {
            console.error('Erro ao recarregar cartões:', err);
        }
    };

    const columns = [
        // { label: 'ID', field: 'id' },
        { label: 'Código do Cartão', field: 'code' },
        {
            label: 'Status',
            field: 'status',
        },
        {
            label: 'Usuário',
            field: 'user',
            renderCell: (value) => {
                if (!value) {
                    return <span style={{ color: 'gray' }}>Não atribuído</span>;
                }

                return (
                    <div>
                        <strong>{value.name}</strong> <br />
                        <span>({value.nickname})</span>
                    </div>
                );
            },
        },
        {
            label: '',
            field: 'actions',
            renderCell: (_, row) => (
                <>
                    {hasPermission('CARDS_VIEW') && (
                        <Button
                            variant="contained"
                            size="small"
                            sx={{ minHeight: '47px' }}
                            onClick={() => {
                                setSelectedCard(row.id);
                                setIsCardDialogOpen(true);
                            }}
                        >
                            Visualizar cartão
                        </Button>
                    )}
                </>
            ),
        },
    ];

    const renderMobileLayout = () => {
        if (cards.length === 0) {
            return (
                <Typography sx={{ textAlign: 'center', mt: 4 }}>
                    Nenhum cartão encontrado.
                </Typography>
            );
        }

        // Render the mobile-friendly list
        return (
            <CardList
                cards={cards}
                onCardClick={(card) => {
                    // e.g. open your CardDialog
                    if (hasPermission('CARDS_VIEW')) {
                        setSelectedCard(card.id);
                        setIsCardDialogOpen(true);
                    }
                }}
            />
        );
    };

    const renderDesktopLayout = () => {
        if (cards.length === 0) {
            return (
                <Typography sx={{ textAlign: 'center', mt: 4 }}>
                    Nenhum cartão encontrado.
                </Typography>
            );
        }

        // Render the table
        return <TableComponent columns={columns} rows={cards} />;
    };

    return (
        <Box>
            <Grid container justifyContent="start">
                <Breadcrumbs aria-label="breadcrumb">
                    <Link underline="hover" color="inherit" href="/">
                        GameUP
                    </Link>
                    <Link underline="hover" color="inherit">
                        Dashboard
                    </Link>
                    <Typography sx={{ color: 'text.primary' }}>
                        Cartões
                    </Typography>
                </Breadcrumbs>
            </Grid>

            <Grid container xs={12} sx={{ mt: 3 }}>
                <Typography
                    sx={{
                        fontFamily: 'Poppins',
                        fontSize: 24,
                        fontWeight: '500',
                        color: '#0C3466',
                        mb: 2,
                    }}
                >
                    Gerenciar Cartões
                </Typography>
            </Grid>

            <Box
                sx={{
                    backgroundColor: '#FFFFFF',
                    borderRadius: '12px',
                    padding: '25px',
                }}
            >
                <Grid
                    container
                    spacing={2}
                    alignItems="center"
                    justifyContent="space-between"
                    wrap="wrap"
                    sx={{ marginBottom: '16px' }}
                >
                    <Grid container item xs={12} md={9} spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={statusFilter}
                                    onChange={handleStatusChange}
                                >
                                    <MenuItem value="">Todos</MenuItem>
                                    <MenuItem value="ASSIGNED">
                                        Atribuídos
                                    </MenuItem>
                                    <MenuItem value="UNASSIGNED">
                                        Não Atribuídos
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* <Grid item xs={12} sm={6} md={3}>
                            <Button
                                variant="contained"
                                endIcon={<AddCircleOutlineOutlinedIcon />}
                                sx={{ minHeight: '47px' }}
                                onClick={() => setIsCreateCardsDialogOpen(true)}
                            >
                                Novo cartão
                            </Button>
                        </Grid> */}
                    </Grid>

                    <Grid item xs={12} md="auto">
                        {hasPermission('CARDS_VIEW') && (
                            <Button
                                variant="contained"
                                endIcon={<ContactPageOutlinedIcon />}
                                sx={{ minHeight: '47px' }}
                                onClick={() => setIsAssociateDialogOpen(true)}
                            >
                                Associar cartão
                            </Button>
                        )}
                    </Grid>
                </Grid>

                {isMobile ? renderMobileLayout() : renderDesktopLayout()}
            </Box>

            <CardAssociationDialog
                open={isAssociateDialogOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        setIsAssociateDialogOpen(false);
                    }
                }}
                onSuccess={handleSuccessAssign}
            />

            <CardDialog
                open={isCardDialogOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        setIsCardDialogOpen(false);
                    }
                }}
                cardId={selectedCard}
                company={user.company}
            />

            <NewCardDialog
                open={isCreateCardsDialogOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        setIsCreateCardsDialogOpen(false);
                    }
                }}
                onSuccess={handleSuccessCreateCards}
                companyId={user.company.id}
            />

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity="success"
                    variant="filled"
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default CardsPage;
